// F4 "change my own password" modal. Same <dialog>+showModal() pattern as
// AddDialog in pages/PoolPage.tsx: opened via a callback ref (not useEffect —
// the effect fires before Preact attaches the ref, which left the dialog
// closed), guarded with `!el.open` because a callback ref also fires on
// re-renders and showModal() throws InvalidStateError on an already-open one.
//
// Every visible string is a prop: the caller is either the Russian staff
// cabinet or the localized contributor cabinet (apps/vista-web/src/main.tsx),
// and this component has no opinion about which.
import { useState } from 'preact/hooks';
import { ApiError } from '../api';

type Props = {
  title: string;
  labelNew: string;
  labelConfirm: string;
  minLenMsg: string;
  mismatchMsg: string;
  cancelLabel: string;
  submitLabel: string;
  savingLabel: string;
  doneMsg: string;
  onSubmit: (password: string) => Promise<void>;
  onClose: () => void;
};

const MIN_LEN = 8;

export function PasswordDialog({
  title,
  labelNew,
  labelConfirm,
  minLenMsg,
  mismatchMsg,
  cancelLabel,
  submitLabel,
  savingLabel,
  doneMsg,
  onSubmit,
  onClose,
}: Props) {
  const openModal = (el: HTMLDialogElement | null) => {
    if (el && !el.open) el.showModal();
  };
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = pwd.length > 0 && pwd.length < MIN_LEN;
  const mismatch = confirm.length > 0 && pwd !== confirm;
  const canSubmit = pwd.length >= MIN_LEN && pwd === confirm && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(pwd);
      setDone(true);
    } catch (e) {
      // A structured domain error (e.g. the request-boundary bad_request from
      // a password shorter than the contract's own minimum) reads better than
      // a raw error code — everything else falls back to the generic string.
      setError(e instanceof ApiError ? e.code : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog ref={openModal} onClose={onClose}>
      <h3>{title}</h3>
      {done ? (
        <div class="result">{doneMsg}</div>
      ) : (
        <>
          <label>{labelNew}</label>
          <input
            type="password"
            autocomplete="new-password"
            value={pwd}
            onInput={(e) => setPwd((e.target as HTMLInputElement).value)}
          />
          <label>{labelConfirm}</label>
          <input
            type="password"
            autocomplete="new-password"
            value={confirm}
            onInput={(e) => setConfirm((e.target as HTMLInputElement).value)}
          />
          {tooShort && <div class="result">{minLenMsg}</div>}
          {mismatch && <div class="result">{mismatchMsg}</div>}
          {error && <div class="result">{error}</div>}
        </>
      )}
      <div class="actions">
        <button class="btn-ghost" onClick={onClose}>
          {cancelLabel}
        </button>
        {!done && (
          <button class="btn-primary" disabled={!canSubmit} onClick={submit}>
            {busy ? savingLabel : submitLabel}
          </button>
        )}
      </div>
    </dialog>
  );
}
